import React, { useEffect } from 'react';
import { Label } from '@atlaskit/form';
import { DateTimePicker } from '@atlaskit/datetime-picker';
import { format, isValid, parse, setHours, setMinutes } from 'date-fns';

const TraneEnsembleDateTimepicker = (props) => {
  const [date, setDate] = React.useState('');
  const [time, setTime] = React.useState('');
  const [isEditing, setIsEditing] = React.useState(props.edit);

  // Handler for date changes
  const handleDateChange = (value) => {
    setDate(value !== '' ? value : null);
    if (props.date) {
      props.date(value !== '' ? value : null);
    }
  };


  // Handler for time changes
  const handleTimeChange = (value) => {
    if (value && value !== '') {
      // Accept value as 'HH:mm' or 'HH:mm:ss', ignore any date or timezone part
      let timeOnly = value;
      // If value contains 'T', split and take time part
      if (timeOnly.includes('T')) {
        timeOnly = timeOnly.split('T')[1];
      }
      // If value contains seconds, trim to HH:mm
      if (timeOnly.length > 5) {
        timeOnly = timeOnly.slice(0,5);
      }
      // Validate time format
      const parsedTime = parse(timeOnly, 'HH:mm', new Date());
      if (isValid(parsedTime)) {
        const formattedTime = format(parsedTime, 'HH:mm');
        setTime(formattedTime);
        if (props.time) {
          props.time(formattedTime); // Always pass as string
        }
      } else {
        setTime('');
        if (props.time) {
          props.time(null);
        }
      }
    } else {
      setTime('');
      if (props.time) {
        props.time(null);
      }
    }
  };

  // Generate combined datetime value for the DateTimePicker
  const getCombinedValue = () => {
    return date && time
      ? `${date}T${time}`
      : '';
  };

  useEffect(() => {
    if (isEditing) {
      if (props.editDate) {
        const newDate = format(props.editDate, 'yyyy-MM-dd');
        setDate(newDate);
      }
      if (props.editTime) {
        // Accept editTime as string (HH:mm or HH:mm:ss or Date)
        let timeString = '';
        if (typeof props.editTime === 'string') {
          // If string contains 'T', split and take time part
          if (props.editTime.includes('T')) {
            timeString = props.editTime.split('T')[1];
          } else {
            timeString = props.editTime;
          }
          // If string contains seconds, trim to HH:mm
          if (timeString.length > 5) {
            timeString = timeString.slice(0,5);
          }
        } else if (props.editTime instanceof Date && isValid(props.editTime)) {
          timeString = format(props.editTime, 'HH:mm');
        }
        setTime(timeString);
      }
    }
  }, [props.editDate, props.editTime]);

  useEffect(() => {
    if (!isEditing) {
      if (props.dateValue) {
        setDate(format(new Date(props.dateValue), 'yyyy-MM-dd'));
      }
      if (props.timeValue) {
        // Accept timeValue as string (HH:mm or HH:mm:ss or Date)
        let timeString = '';
        if (typeof props.timeValue === 'string') {
          if (props.timeValue.includes('T')) {
            timeString = props.timeValue.split('T')[1];
          } else {
            timeString = props.timeValue;
          }
          if (timeString.length > 5) {
            timeString = timeString.slice(0,5);
          }
        } else if (props.timeValue instanceof Date && isValid(props.timeValue)) {
          timeString = format(props.timeValue, 'HH:mm');
        }
        setTime(timeString);
      }
    }
  }, [props.dateValue, props.timeValue]);

  return (
    <>
      <Label htmlFor="datetime">{props.label}</Label>
      <DateTimePicker
        name={props.name}
        id={props.id}
        clearControlLabel="Clear"
        value={getCombinedValue()}
        onChange={(value) => {
          const [datePart, timePart] = value.split('T');
          setIsEditing(false);
          if (datePart) handleDateChange(datePart);
          if (timePart) handleTimeChange(timePart);
        }}
        datePickerProps={{
          shouldShowCalendarButton: true,
          placeholder: 'Select date',
          value: date,
          onChange: handleDateChange,
        }}
        timePickerProps={{
          placeholder: 'Select time',
          timeFormat: props.timeFormat,
          times: generateTimes(),
          timeIsEditable: true,
          value: time,
          onChange: handleTimeChange,
        }}
        isInvalid={props.errorDateFields}
      />
    </>
  );
};

// Helper function to generate time options
const generateTimes = () => {
  const times = [];
  for (let i = 0; i < 24; i++) {
    times.push(format(setMinutes(setHours(new Date(), i), 0), 'HH:mm'));
    times.push(format(setMinutes(setHours(new Date(), i), 30), 'HH:mm'));
  }
  return times;
};

export default TraneEnsembleDateTimepicker;